// ================================
// DaywiseDistanceReport.jsx (FULL – with TOTAL, CSV & DateRangePicker)
// ================================

import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DownloadExcelButton from "components/ReportUI/Downloadexcelbutton";
import MDSnackbar from "components/MDSnackbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DaywiseDistanceReportsTable from "./daywisedistancereporttable";
import { daywisedistancereportendpoint, accountmy } from "config";
import DateRangePicker from "layouts/DateTimerange/dateTimeRange";
import DhundhooLoader from "../Loader/Loader";

// ================================
// Utils
// ================================

const formatYMD = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const getDatesBetween = (start, end) => {
  const dates = [];
  const current = new Date(start);
  while (current <= end) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return dates;
};

// Pivot using backend response
const pivotData = (apiData, dateList) => {
  return apiData.map((item) => {
    const row = {
      thingName: item.thingName,
      TOTAL: Number(item.totalDistance || 0).toFixed(2),
    };

    dateList.forEach((d) => {
      const key = formatYMD(d);
      row[key] = Number(item.distances?.[key] || 0).toFixed(2);
    });

    return row;
  });
};

// ✅ Helper to safely parse "YYYY-MM-DDTHH:mm" string to local Date object
const parseDateStr = (dateStr) => {
  if (!dateStr) return new Date();
  const [y, m, d] = dateStr.split("T")[0].split("-").map(Number);
  return new Date(y, m - 1, d);
};

// ================================
// Component
// ================================

function DaywiseDistanceReport() {
  const today = new Date();

  const getTodayDateString = () => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const todayStr = getTodayDateString();
  const defaultStart = `${todayStr}T00:00`;
  const defaultEnd = `${todayStr}T23:59`;

  // ✅ Set to null initially for placeholder
  const [startTime, setStartTime] = useState(defaultStart);
  const [endTime, setEndTime] = useState(defaultEnd);
  const [orgName, setOrgName] = useState("");

  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [dateList, setDateList] = useState([]);

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);

  // ================================
  // Fetch Account
  // ================================

  useEffect(() => {
    const fetchAccount = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(accountmy, {
          headers: { "x-dhundhoo-session": token },
        });
        setOrgName(res?.data?.orgInfo?.orgName || "");
      } catch (e) {
        console.error(e);
      }
    };
    fetchAccount();
  }, []);

  // ================================
  // Fetch Logs
  // ================================

  const fetchLogs = async () => {
    // ✅ Validation for dates
    if (!startTime || !endTime) {
      setErrorMessage("Please select a date range first");
      setErrorSB(true);
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const token = localStorage.getItem("token");

      // Convert string states back to native Date objects for existing logic
      const startDateObj = parseDateStr(startTime);
      const endDateObj = parseDateStr(endTime);

      const payload = {
        date: formatYMD(today),
        zone: "Asia/Kolkata",
        startTime: formatYMD(startDateObj),
        endTime: formatYMD(endDateObj),
        orgName,
      };

      const res = await axios.post(daywisedistancereportendpoint, payload, {
        headers: {
          "x-dhundhoo-session": token,
          "Content-Type": "application/json",
        },
      });

      const dates = getDatesBetween(startDateObj, endDateObj);
      setDateList(dates);

      // Columns
      const dynamicColumns = dates.map((d) => ({
        Header: d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }),
        accessor: formatYMD(d),
        align: "center",
      }));

      setColumns([
        { Header: "Vehicle Number", accessor: "thingName", width: "18%" },
        ...dynamicColumns,
        { Header: "TOTAL (Km)", accessor: "TOTAL", align: "center" },
      ]);

      const pivotRows = pivotData(res.data, dates);
      setRows(pivotRows);
      setSuccessSB(true);
    } catch (e) {
      console.error(e);
      setErrorMessage("Failed to load distance report");
      setErrorSB(true);
    } finally {
      setLoading(false);
    }
  };

  // ================================
  // CSV Export (Same UI Format)
  // ================================

  const exportToCSV = () => {
    if (!rows.length) return;

     const formatFileDate = (dateTime) => {
  if (!dateTime) return "";

  const date = new Date(dateTime);

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

const formattedDate = `${formatFileDate(startTime)}_to_${formatFileDate(endTime)}`;

    const headers = [
      "Vehicle Number",
      ...dateList.map((d) => d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })),
      "TOTAL (Km)",
    ];

    const csvRows = rows.map((row) => {
      return [
        row.thingName,
        ...dateList.map((d) => row[formatYMD(d)]),
        row.TOTAL,
      ].join(",");
    });

    const csvContent = [headers.join(","), ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Daywise_Distance_Report_${formattedDate}.csv`;
    link.click();
  };

  const handleClear = () => {
    setStartTime(defaultStart); // ✅ Reset back to today
    setEndTime(defaultEnd);
    setRows([]);
    setColumns([]);
    setDateList([]);
    setErrorMessage("");
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Grid container spacing={2} alignItems="center">

                    {/* ✅ Custom DateRangePicker */}
                    <Grid item xs={12} md={6}>
                      <div style={{ width: '46%' }}>
                        <DateRangePicker
                          startTime={startTime}
                          endTime={endTime}
                          onChange={({ startTime: s, endTime: e }) => {
                            setStartTime(s);
                            setEndTime(e);
                          }}
                          disabled={loading}
                        />
                      </div>
                    </Grid>

                    {/* Action Buttons */}
                    <Grid item xs={4} sm={2} md={2}>
                      <MDButton fullWidth variant="gradient" color="warning" size="small" onClick={fetchLogs} disabled={loading}>
                        {loading ? <CircularProgress size={20} color="inherit" /> : "LOAD"}
                      </MDButton>
                    </Grid>

                    <Grid item xs={4} sm={2} md={2}>
                      <MDButton fullWidth variant="outlined" color="error" size="small" onClick={handleClear} disabled={loading}>
                        CLEAR
                      </MDButton>
                    </Grid>

                    <Grid item xs={4} sm={2} md={2}>
                      <DownloadExcelButton fullWidth label="CSV" onClick={exportToCSV} disabled={loading || rows.length === 0} />
                    </Grid>

                  </Grid>
                </LocalizationProvider>
              </MDBox>

              {errorMessage && (
                <MDBox px={3} pb={2}>
                  <Alert severity="error">{errorMessage}</Alert>
                </MDBox>
              )}

              <MDBox px={3} pb={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={5}>
                    <DhundhooLoader />
                  </MDBox>
                ) : (
                  columns.length > 0 && (
                    <DaywiseDistanceReportsTable table={{ columns, rows }} entriesPerPage={false} showTotalEntries />
                  )
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <MDSnackbar
        color="success"
        icon="check"
        title="Distance Report"
        content="Data loaded successfully"
        open={successSB}
        onClose={() => setSuccessSB(false)}
        close={() => setSuccessSB(false)}
        bgWhite
      />

      <MDSnackbar
        color="error"
        icon="warning"
        title="Distance Report"
        content={errorMessage || "Failed to load data"}
        open={errorSB}
        onClose={() => setErrorSB(false)}
        close={() => setErrorSB(false)}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default DaywiseDistanceReport;